import {
  type APIOrganizationUser,
  type ArdoqId,
  type GroupingRule,
  ViewIds,
} from '@ardoq/api-types';
import { returnNull } from '@ardoq/common-helpers';
import { Features, hasFeature } from '@ardoq/features';
import { isPresentationMode } from 'env';
import {
  CommonRelationshipsDiagramEmptyState,
  getDistinctReferenceKey,
  getEmptyTraversalAndGroupingResult,
  RelationshipDiagramViewModel,
  ViewpointRelationshipDiagramViewModelEmptyState,
} from '@ardoq/graph';
import { DEFAULT_ARDOQ_I18NLOCALE } from '@ardoq/i18n';
import { GraphComponentViewLegendProps } from '@ardoq/view-legend';
import { SmoothingPolicy } from '@ardoq/yfiles';
import { noop } from 'lodash';
import { EMPTY_LOADED_SCENARIO_DATA } from 'loadedScenarioData$';
import {
  type FormattingAndLabelRules,
  getEmptyGraphModel,
  type ScopeDiffState,
} from '@ardoq/data-model';
import { EMPTY_CONTEXT_SHAPE } from 'streams/context/ContextShape';
import {
  emptyComponentsState,
  emptyScenarioRelatedComponentIds,
} from 'streams/components/components$';
import { loadedGraphOperations } from 'traversals/loadedGraphOperations';
import { DEFAULT_VIEWPOINT_MODE_SETTINGS } from 'views/environmentals/viewSettings/defaultStreamState';
import {
  GraphEventHandlers,
  GraphEventType,
} from 'tabview/graphComponent/graphEvents/types';
import type { SharedBlockDiagramsRenderingDataSource } from './shared/sharedGlobalStreams';
import type { ModernizedViewModelResetStreamShape } from 'tabview/blockDiagram/modernizedViewModel$';
import type { ViewDataLimitCheck } from './viewModel$/types';
import type { ViewpointRelationshipDiagramScenarioData } from 'tabview/relationshipsDiagramsViewModel/types';

export const NO_USERS: APIOrganizationUser[] = [];
export const NO_ORG_USERS_BY_ID: Record<ArdoqId, APIOrganizationUser> = {};
export const NO_FORMATTING_AND_LABEL_RULES: FormattingAndLabelRules['rules'] =
  [];
export const NO_GROUPING_RULES: GroupingRule[] = [];

export const NO_SCOPE_DIFF: ScopeDiffState = {
  isLoading: false,
  scopeDiff: null,
};

export const NO_SCENARIO: ViewpointRelationshipDiagramScenarioData = {
  loadedScenarioData: EMPTY_LOADED_SCENARIO_DATA,
  scenarioRelatedComponents: { componentIds: [] },
  showScopeRelated: false,
};

export const DATA_LIMIT_CHECK_DISABLED: ViewDataLimitCheck = {
  isEnabled: false,
  isPresentationMode: false,
  hasUserBypassed: false,
};

export const buildViewDataLimitCheck = (
  hasUserBypassed: boolean
): ViewDataLimitCheck => ({
  isEnabled: hasFeature(Features.VIEW_DATA_LIMIT_BEFORE_BUILD),
  isPresentationMode: isPresentationMode(),
  hasUserBypassed,
});

export const EDGE_SMOOTHING_LENGTH = 16;
export const SMOOTH_PATH_OPTIONS = {
  smoothingLength: EDGE_SMOOTHING_LENGTH,
  smoothingPolicy: SmoothingPolicy.SYMMETRIC,
};

export const COMPONENT_STYLE_LABEL_SHAPE = 'Shape';
export const COMPONENT_STYLE_LABEL_ICON = 'Icon';

const EMPTY_SOURCE_STREAM_STATE = {
  context: EMPTY_CONTEXT_SHAPE,
  components: emptyComponentsState,
  scenarioRelatedComponentIds: emptyScenarioRelatedComponentIds,
  graphModel: getEmptyGraphModel(),
  loadedScenarioData: EMPTY_LOADED_SCENARIO_DATA,
  scenarioRelatedComponents: { componentIds: [] },
  showScopeRelated: false,
  rawGraph: getEmptyTraversalAndGroupingResult(),
  nodeLabelsData: new Map(),
  performanceTrackingId: null,
};

const EMPTY_GRAPH_ITEMS_MODEL = {
  byId: new Map(),
  add: [],
  update: [],
  remove: [],
};
const EMPTY_GRAPH_ITEMS = {
  groups: EMPTY_GRAPH_ITEMS_MODEL,
  nodes: EMPTY_GRAPH_ITEMS_MODEL,
  edges: EMPTY_GRAPH_ITEMS_MODEL,
};

const EMPTY_VIEW_MODEL: RelationshipDiagramViewModel = {
  ...EMPTY_GRAPH_ITEMS,
  errors: [],
  hasClones: false,
  expandedStateChangingGroupId: null,
  urlFieldValuesByComponentId: new Map(),
  urlFieldValuesByReferenceId: new Map(),
  nodeLabelsData: new Map(),
  componentIdsOfCollapsedGroups: [],
  traversedIncomingReferenceTypes: [],
  traversedOutgoingReferenceTypes: [],
  referenceTypes: [],
  noConnectedComponents: false,
};

export const COMMON_RELATIONSHIPS_DIAGRAM_EMPTY_STATE: CommonRelationshipsDiagramEmptyState<RelationshipDiagramViewModel> =
  {
    viewModel: EMPTY_VIEW_MODEL,
    streamState: {
      ...EMPTY_SOURCE_STREAM_STATE,
      skipRender: false,
      isUpdatingAppState: false,
      getDistinctReferenceKey,
    },
    loadedGraph: loadedGraphOperations.getEmpty(),
    viewId: ViewIds.NONE,
    viewInstanceId: '',
    noContext: false,
    scopeDiffState: {
      isLoading: false,
      scopeDiff: null,
    },
    i18nLocale: DEFAULT_ARDOQ_I18NLOCALE,
  };

export const EMPTY_GRAPH_COMPONENT_LEGEND_PROPS: GraphComponentViewLegendProps =
  {
    displayShapesInsteadOfIcons: false,
    componentTypes: [],
    referenceTypes: [],
    activeConditionalFormatting: {
      components: [],
      references: [],
      tags: [],
    },
    activeDiffMode: null,
  };

export const VIEWPOINT_MODE_RELATIONSHIPS_DIAGRAM_EMPTY_STATE: ViewpointRelationshipDiagramViewModelEmptyState =
  {
    ...EMPTY_GRAPH_ITEMS,
    errors: [],
    hasClones: false,
    expandedStateChangingGroupId: null,
    componentIdsOfCollapsedGroups: [],
    nodeLabelsData: new Map(),
    edgeLabelsData: new Map(),
    urlFieldValuesByComponentId: new Map(),
    urlFieldValuesByReferenceId: new Map(),
    nodeColorsByGraphItemId: new Map(),
    conditionalFormattingState: new Map(),
    edgeColorsByReferenceModelId: new Map(),
    representationDataByComponentId: new Map(),
    context: EMPTY_CONTEXT_SHAPE,
    graphModel: getEmptyGraphModel(),
    loadedScenarioData: EMPTY_LOADED_SCENARIO_DATA,
    scenarioRelatedComponents: { componentIds: [] },
    showScopeRelated: false,
    rawGraph: getEmptyTraversalAndGroupingResult(),
    allGroupIds: [],
    getRepresentationData: returnNull,
    performanceTrackingId: null,
    loadedGraph: loadedGraphOperations.getEmpty(),
    viewId: ViewIds.NONE,
    scopeDiffState: {
      isLoading: false,
      scopeDiff: null,
    },
    viewInstanceId: '',
    scopeDataParams: null,
    formattingFilters: [],
    i18nLocale: DEFAULT_ARDOQ_I18NLOCALE,
  };

export const ROOT_NODE_ID = 'root';

export const WORKSPACE_MODE_RELATIONSHIPS_VIEW_EMPTY_STATE_BASE = {
  ...EMPTY_VIEW_MODEL,
  ...EMPTY_SOURCE_STREAM_STATE,
  noContext: false,
  scopeDiffState: {
    isLoading: false,
    scopeDiff: null,
  },
};

export const EMPTY_GRAPH_EVENT_HANDLERS: GraphEventHandlers = {
  [GraphEventType.ITEM_CLICKED]: noop,
  [GraphEventType.ITEM_DOUBLE_CLICKED]: noop,
  [GraphEventType.ITEM_RIGHT_CLICKED]: noop,
  [GraphEventType.MULTI_SELECTION_FINISHED]: noop,
};

export const DEFAULT_RENDERING_DATA_CONFIG: SharedBlockDiagramsRenderingDataSource =
  {
    isContextChange: false,
    clearEdges: false,
    scenarioName: '',
    isViewpointMode: true,
  };

export const EMPTY_MODERNIZED_RESET_PAYLOAD: ModernizedViewModelResetStreamShape =
  {
    context: EMPTY_CONTEXT_SHAPE,
    components: emptyComponentsState,
    scenarioRelatedComponentIds: emptyScenarioRelatedComponentIds,
    graphModel: getEmptyGraphModel(),
    scenario: [
      EMPTY_LOADED_SCENARIO_DATA,
      { componentIds: [] },
      { showScopeRelated: false },
      { scopeDiff: null, isLoading: false },
    ],
    loadedGraph: loadedGraphOperations.getEmpty(),
    i18nLocale: DEFAULT_ARDOQ_I18NLOCALE,
    scopeDataParams: null,
    users: [],
    orgUsersById: {},
    viewSettings:
      DEFAULT_VIEWPOINT_MODE_SETTINGS[ViewIds.MODERNIZED_BLOCK_DIAGRAM],
    isGraphCollapsible: false,
    formattingAndLabelRules: [],
  };
